import type { MatchAlertCadence } from '@/lib/matchCadence'

export type SmsConsentStatus = 'unknown' | 'opted_in' | 'opted_out'
// 'paused' (mig 416): opted out because their text MIGHT ask us to stop; the listing agent decides.
// 'lead_source' (mig 437): a NEW lead from a source the brokerage ticked in Settings › Lead Routing.
export type SmsConsentSource = 'web_form' | 'agent_attested' | 'imported' | 'reply_start' | 'reply_stop' | 'paused' | 'lead_source' | null

export interface Contact {
  id: string
  organization_id: string
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  secondary_phone: string | null
  // ⚠️ NO ADDRESS COLUMNS HERE. mig 285 briefly put mailing_* on `contacts`; RIZ038 asked
  // for one address MODEL with a type, because a contact has a mailing address AND second
  // homes and investment properties — which four columns can never hold. mig 287 replaced
  // them with `contact_addresses` (see ContactAddress below). Adding a mailing_* column
  // back would recreate the two-parallel-systems problem the ticket exists to prevent.
  avatar_url: string | null
  type: 'lead' | 'client' | 'past_client' | 'vendor' | 'agent' | 'other' | null
  status: string | null
  lead_source: string | null
  assigned_agent_id: string | null
  lead_score: number
  /**
   * The tier IN EFFECT — a human's `lead_tier_override` if one is set, otherwise the band
   * computed from `lead_score`. Generated at the database (mig 284), so the nightly
   * scorer cannot revert an override; it only ever writes `lead_score`.
   */
  lead_tier: 'hot' | 'warm' | 'nurture' | 'cold'
  /** A tier a person set by hand. null = "let the score decide". */
  lead_tier_override: 'hot' | 'warm' | 'nurture' | 'cold' | null
  lead_tier_override_at: string | null
  lead_tier_override_by: string | null
  /**
   * Where this person is with the brokerage, in Zillow's words (RIZ037, mig 417) — a key
   * in the brokerage's `contact_stages` list, resolved to words through
   * src/lib/contactStages.ts. Moved automatically (forward only) and by hand. NULL for
   * people who are never a lead (agents, vendors). The `stage_*` stamps are written by
   * the database and never by this app.
   */
  stage: string | null
  stage_source: 'auto' | 'manual' | 'import' | 'backfill' | null
  stage_reason: string | null
  stage_set_at: string | null
  stage_set_by: string | null
  // Recency-excluded quality (mig 112) — drives follow-up cadence for leads.
  quality_score: number
  cadence_tier: 'hot' | 'warm' | 'nurture' | 'cold'
  last_scored_at: string | null
  is_bona_fide_client: boolean
  /**
   * The pinned "Heads up" strip (RIZ074, mig 381). PLAIN TEXT, max 500, and NULL
   * when there is nothing to say - the strip renders only when this is non-empty,
   * which is what stops it becoming furniture on every record. Write it through
   * normalizeHeadsUp(): the column REFUSES an empty string on purpose.
   */
  heads_up: string | null
  buyer_criteria: BuyerCriteria
  social_profiles: Record<string, string>
  tags: string[]
  lpma: LPMAScore
  custom_fields: Record<string, unknown>
  last_contacted_at: string | null
  // Follow-up cadence override (F3, mig 112). null interval = inherit org policy.
  follow_up_interval_days: number | null
  follow_up_paused: boolean
  // SMS consent (migration 038)
  sms_consent_status: SmsConsentStatus
  sms_consent_source: SmsConsentSource
  sms_consent_at: string | null
  sms_consent_detail: string | null
  // Portal-alert snooze (mig 132) — non-null muted_at silences all portal
  // tracking-intelligence alerts for this contact (events still log + score).
  portal_alerts_muted_at: string | null
  portal_alerts_muted_by: string | null
  /**
   * How often THIS CLIENT is emailed their new MLS saved-search matches
   * (mig 302, Dakri 2026-08-21). immediate = on the run that finds it (within
   * 15 minutes, matching runs four times an hour) | daily = one digest a day |
   * off = no client email, the agent is still notified.
   *
   * Not sufficient on its own: the org's `smart_match_email` autonomy setting
   * must also be act_and_notify or full_auto, or match-properties keeps parking
   * a draft in /review. Resolve through src/lib/matchCadence.ts.
   */
  match_alert_cadence: MatchAlertCadence
  /**
   * When someone marked this contact "Not a buyer" (mig 358, RIZ058), which hides the
   * "No search set up for this buyer" flag on the record. NULL = never dismissed, or undone.
   * A display decision only — match-properties does not read it.
   */
  buyer_search_prompt_dismissed_at: string | null
  /**
   * When this buyer's search was shared with the client (mig 474, RIZ144). NULL = not shared yet:
   * nothing has gone to the client and their portal does not show it; only the agent's preview
   * reads it. Set by "Share with <client>" / "Send these to <client>" and by the client's own
   * portal save; never cleared. Resolve through src/lib/searchShare.ts.
   */
  buyer_search_shared_at: string | null
  created_at: string
  updated_at: string
}

export interface BuyerCriteria {
  price_min?: number
  price_max?: number
  beds_min?: number
  baths_min?: number
  sqft_min?: number
  acreage_min?: number
  // Read by match-properties as a HARD filter since the adversarial-review fix;
  // was missing from this type + the card until R4-3 (pre-existing gap).
  garage_min?: number
  locations?: string[]
  // RIZ059 (migs 372/473). The search's fields are DEFINED in
  // supabase/functions/_shared/search-filters.ts; this type only describes the stored blob.
  sqft_max?: number
  acreage_max?: number
  year_built_min?: number
  year_built_max?: number
  dom_max?: number
  postal_codes?: string[]
  subdivisions?: string[]
  school_districts?: string[]
  property_styles?: string[]
  exclude_hoa?: boolean
  must_haves?: string[]
  dealbreakers?: string[]
  property_types?: string[]
  timeline?: string
  pre_approved?: boolean
  lender?: string
}

// LPMA² — Lead Qualification Framework
// Captured during calls by AI, feeds into lead scoring (up to 35/100 points)
// Also used in agent whisper briefings before connecting calls
export interface LPMAScore {
  location: string | null           // L — where they want to buy/sell
  price_range: string | null        // P — budget or expected sale price
  motivation: string | null         // M — why are they moving? (relocation, upsizing, divorce, investment, etc.)
  has_agent: boolean | null         // A — do they already have a real estate agent?
  mortgage_status: string | null    // M — pre-approved, shopping lenders, cash, needs pre-approval, unknown
  wants_appointment: boolean | null // A — did they express interest in meeting/showing?
}

export const MORTGAGE_STATUSES = [
  { value: 'pre_approved', label: 'Pre-Approved' },
  { value: 'shopping_lenders', label: 'Shopping Lenders' },
  { value: 'cash', label: 'Cash Buyer' },
  { value: 'needs_preapproval', label: 'Needs Pre-Approval' },
  { value: 'unknown', label: 'Unknown' },
]

export const MOTIVATION_TYPES = [
  { value: 'relocation', label: 'Relocation' },
  { value: 'upsizing', label: 'Upsizing' },
  { value: 'downsizing', label: 'Downsizing' },
  { value: 'first_home', label: 'First Home' },
  { value: 'investment', label: 'Investment' },
  { value: 'divorce', label: 'Divorce' },
  { value: 'retirement', label: 'Retirement' },
  { value: 'rental_to_own', label: 'Renting → Buying' },
  { value: 'other', label: 'Other' },
]

export type ContactType = Contact['type']

export const CONTACT_TYPES: { value: NonNullable<ContactType>; label: string }[] = [
  { value: 'lead', label: 'Lead' },
  { value: 'client', label: 'Client' },
  { value: 'past_client', label: 'Past Client' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'agent', label: 'Agent' },
  { value: 'other', label: 'Other' },
]

export const LEAD_SOURCES: { value: string; label: string }[] = [
  // ⚠️ THIS ORDER IS THE SEED ORDER. lead-sources.test.mjs compares this array, in
  // sequence, against the ensure_lead_sources() rows — the DB stores a `sort` per row and
  // a brokerage can rename its own labels, so the two drifting apart is a real bug rather
  // than a style point.
  { value: 'sign_call', label: 'Sign Call' },
  { value: 'zillow', label: 'Zillow' },
  { value: 'realtor_com', label: 'Realtor.com' },
  { value: 'redfin', label: 'Redfin' },
  { value: 'homes_com', label: 'Homes.com' },
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'social', label: 'Social Media' },
  { value: 'open_house', label: 'Open House' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'manual', label: 'Manual Entry' },
  // ⚠️ WRITTEN BY twilio-voice-webhook, NOT CHOSEN BY A HUMAN (RIZ019, mig 289/290).
  // Derived from tracking_numbers.line_type via _shared/lead-source.ts, so these VALUES
  // are a contract with that function — rename one here and every lead already stamped
  // with it becomes an unlabelled string. Labels are free to change; values are not.
  // They are seeded is_system for the same reason sign_call is: a brokerage deleting one
  // would leave those writes pointing at a source with no row.
  //
  // APPENDED rather than grouped beside sign_call on purpose. Grouping would mean
  // renumbering `sort` on rows that already exist in every org, and a brokerage that has
  // reordered its own list would silently lose that. New entries go on the end.
  { value: 'agent_call', label: "Agent's Line" },
  { value: 'office_call', label: 'Office Line' },
]

export const TIER_COLORS: Record<string, string> = {
  hot: 'bg-red-500/20 text-red-400',
  warm: 'bg-orange-500/20 text-orange-400',
  nurture: 'bg-blue-500/20 text-blue-400',
  cold: 'bg-zinc-500/20 text-zinc-400',
}

// Lead-score reference for UI explanation. DISPLAY ONLY — the authoritative
// scoring math + weights live in supabase/functions/_shared/score.ts
// (SCORE_WEIGHTS). Keep these in sync with that file.
export const LEAD_SCORE_TIERS: { tier: string; label: string; min: number }[] = [
  { tier: 'hot', label: 'Hot', min: 80 },
  { tier: 'warm', label: 'Warm', min: 60 },
  { tier: 'nurture', label: 'Nurture', min: 40 },
  { tier: 'cold', label: 'Cold', min: 0 },
]

/**
 * One address belonging to a contact — where they get their post, a rental that was never
 * listed, a second home (RIZ038, mig 287).
 *
 * ⚠️ AN ADDRESS THAT *IS* AN MLS LISTING DOES NOT LIVE HERE. That is `contact_properties`,
 * which records a transacting SIDE (selling/buying) against a (dataset_code, listing_key)
 * pair — a different dimension. Keeping a listing link on this table too would give "their
 * house at 123 Main, which is MLS #456" two homes, which is the duplication RIZ038 names.
 *
 * Every address part is independently nullable: a row typed during a phone call must still
 * save. Compose through src/lib/mailingAddress.ts, never at a call site, or a half-filled
 * address renders as ", , ID".
 */
export interface ContactAddress {
  id: string
  organization_id: string
  contact_id: string
  street: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  /** A contact may have at most ONE 'mailing' — enforced by a partial unique index. */
  address_type: ContactAddressType
  /** What the agent calls it: "Rental in Boise". Optional on purpose. */
  label: string | null
  created_at: string
  updated_at: string
  created_by: string | null
}

export type ContactAddressType = 'mailing' | 'residence' | 'investment' | 'other'

/** Mirrors contact_addresses_type_check (mig 287) — the picker may offer only these. */
export const CONTACT_ADDRESS_TYPES: { value: ContactAddressType; label: string }[] = [
  { value: 'mailing', label: 'Mailing address' },
  { value: 'residence', label: 'Home' },
  { value: 'investment', label: 'Investment property' },
  { value: 'other', label: 'Other' },
]

/** The four tier values. Mirrors contacts_lead_tier_override_check (mig 284). */
export type LeadTier = 'hot' | 'warm' | 'nurture' | 'cold'

/**
 * The tier the SCORE alone would give — i.e. what the system suggests.
 *
 * Not the tier in effect: `contact.lead_tier` is the database's answer and already
 * resolves a human's override (mig 284). This exists so the UI can show BOTH, which is
 * the whole of the RIZ033 ruling — a person seeing "you set this to Hot; the score says
 * Cold" can judge whether their override is still right, where a bare chip cannot.
 *
 * ⚠️ THE BANDS ARE THE DATABASE'S. This reads LEAD_SCORE_TIERS, which restates mig 284's
 * generated expression for display; tests/unit/lead-tier-override.test.mjs parses that
 * migration and fails if the two ever drift. Before that pin the two were kept in step by
 * a comment asking politely.
 */
export function tierForScore(score: number): LeadTier {
  const band = LEAD_SCORE_TIERS.find((t) => score >= t.min)
  return (band?.tier ?? 'cold') as LeadTier
}

export const LEAD_SCORE_DIMENSIONS: { label: string; max: number; hint: string }[] = [
  { label: 'LPMA² qualification', max: 35, hint: 'Location, price, motivation, has-agent, mortgage, appointment — captured on calls' },
  { label: 'Engagement', max: 15, hint: 'Calls, emails, and client-portal activity (views, saves, shares — recent activity counts most)' },
  { label: 'Timeline', max: 15, hint: 'How soon they plan to buy or sell' },
  { label: 'Revenue', max: 15, hint: 'Budget / max price' },
  { label: 'Call sentiment', max: 10, hint: 'Tone of their most recent call' },
  { label: 'Recency', max: 10, hint: 'How recently you last contacted them' },
]

export const TIMELINE_OPTIONS: { value: string; label: string }[] = [
  { value: 'immediate', label: 'Immediate' },
  { value: '1-3months', label: '1–3 months' },
  { value: '3-6months', label: '3–6 months' },
  { value: '6-12months', label: '6–12 months' },
  { value: '12plus', label: '12+ months' },
]

// Humanize a raw enum value (underscores → spaces, Title Case) so an unknown DB
// value never renders as a raw token. Fallback for the label helpers below.
function humanizeEnum(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Format enum values to their canonical label from the lists above — never a
// hardcoded label map at the call site (HELLA no-hardcoded-data rule).
export function labelForType(value: string | null | undefined): string {
  if (!value) return ''
  return CONTACT_TYPES.find((t) => t.value === value)?.label ?? humanizeEnum(value)
}
export function labelForSource(value: string | null | undefined): string {
  if (!value) return ''
  return LEAD_SOURCES.find((s) => s.value === value)?.label ?? humanizeEnum(value)
}
export function labelForTimeline(value: string | null | undefined): string {
  if (!value) return ''
  return TIMELINE_OPTIONS.find((t) => t.value === value)?.label ?? humanizeEnum(value)
}
